import type { SwitchUserContextResponse } from "@lib/ShiOneClient";

const STORAGE_KEY = "storefront-switch.cart-summary";
// Kept apart from the dismissible summary: a retry needs it until the cart migration succeeds.
const PENDING_SOURCE_CURRENCY_KEY = "storefront-switch.pending-source-currency";

export interface StorefrontCurrencySummary {
  code: string;
  symbol: string;
  locale: string;
}

export interface CartSwitchSummary {
  userId: string;
  cartMigrationFailed: boolean;
  removedCount: number;
  repriced: {
    lineNumber: number;
    productName?: string;
    previousUnitPrice: number;
    unitPrice: number;
  }[];
  previousCurrency?: StorefrontCurrencySummary;
  /** Set once the post-switch notice has been seen; the cart page still shows the details. */
  announced?: boolean;
  /** Impersonated switches leave the real user's cart untouched. */
  cartNotRecalculated?: boolean;
}

/**
 * Owner of a summary: the authenticated subject plus the active impersonation target, because
 * entering or leaving impersonation reloads without changing the Auth0 subject.
 */
function summaryOwner(userId: string) {
  const impersonatedUser = localStorage.getItem("impersonateUser") ?? "";
  const impersonatedAccount = localStorage.getItem("impersonateAccount") ?? "";
  return `${userId}|${impersonatedUser}|${impersonatedAccount}`;
}

/**
 * Repriced lines are only known from the switch response: the legacy cart read clears the
 * IsChanged flag, so the summary is carried across the post-switch reload in session storage.
 */
export function saveCartSwitchSummary(
  response: SwitchUserContextResponse,
  userId?: string,
  cartNotRecalculated = false,
  previousCurrency?: StorefrontCurrencySummary,
) {
  if (!userId) {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(PENDING_SOURCE_CURRENCY_KEY);
    return;
  }

  if (response.cartMigrationFailed && previousCurrency) {
    sessionStorage.setItem(
      PENDING_SOURCE_CURRENCY_KEY,
      JSON.stringify({ userId: summaryOwner(userId), currency: previousCurrency }),
    );
  } else {
    sessionStorage.removeItem(PENDING_SOURCE_CURRENCY_KEY);
  }

  if (cartNotRecalculated) {
    const summary: CartSwitchSummary = {
      userId: summaryOwner(userId),
      cartMigrationFailed: false,
      removedCount: 0,
      repriced: [],
      cartNotRecalculated: true,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(summary));
    return;
  }

  const repriced = (response.cart?.changes ?? [])
    .filter((change) => change.change === "Repriced")
    .map((change) => ({
      lineNumber: change.lineNumber,
      productName: change.productName,
      previousUnitPrice: change.previousUnitPrice,
      unitPrice: change.unitPrice,
    }));
  const removedCount = response.cart?.removedCount ?? 0;

  if (
    !response.cartMigrationFailed &&
    repriced.length === 0 &&
    removedCount === 0
  ) {
    sessionStorage.removeItem(STORAGE_KEY);
    return;
  }

  const summary: CartSwitchSummary = {
    userId: summaryOwner(userId),
    cartMigrationFailed: response.cartMigrationFailed,
    removedCount,
    repriced,
    ...(repriced.length > 0 && previousCurrency ? { previousCurrency } : {}),
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(summary));
}

export function readCartSwitchSummary(
  userId?: string,
): CartSwitchSummary | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const summary = JSON.parse(raw) as CartSwitchSummary;
    if (!userId || summary.userId !== summaryOwner(userId)) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return summary;
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/**
 * Currency of the storefront a failed cart migration started from. The cart still holds that
 * storefront's prices, so a retry labels them with it rather than the now-current currency.
 */
export function readPendingSourceCurrency(
  userId?: string,
): StorefrontCurrencySummary | undefined {
  const raw = sessionStorage.getItem(PENDING_SOURCE_CURRENCY_KEY);
  if (!raw || !userId) {
    return undefined;
  }
  try {
    const pending = JSON.parse(raw) as {
      userId: string;
      currency: StorefrontCurrencySummary;
    };
    return pending.userId === summaryOwner(userId) ? pending.currency : undefined;
  } catch {
    sessionStorage.removeItem(PENDING_SOURCE_CURRENCY_KEY);
    return undefined;
  }
}

export function clearCartSwitchSummary() {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function markCartSwitchSummaryAnnounced(userId?: string) {
  const summary = readCartSwitchSummary(userId);
  if (summary && !summary.announced) {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...summary, announced: true }),
    );
  }
}
